import { inject, injectable } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import type { ITopCompanyServerData, ITopParentsPayload } from '@/creditflowresearch/application/TopCompanies/interfaces';
import {
  ICompaniesListPayload,
  ICompaniesListResponse,
  ICompanyListAggregateResponse,
} from '@/creditflowresearch/domain/vo/Company/ICompany';
import { ICompany } from '@/condor/domain/vo/Finsight/Companies/Company';
import { ICompanyByViewIdResponse } from '@/creditflowresearch/domain/vo/ICompany';
import {
  ICorporateTopFilters,
  ICorporateTtmFilter,
  ITopCompaniesCorporateResponse,
  ITopCompaniesPayload,
} from '@/creditflowresearch/application/TopCompanies/corporateTopCompanies/interfaces';
import { withServerCache } from '@/creditflowresearch/infrastructure/cache/withServerCache';

@injectable()
class CompanyRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {}

  getCompanyByViewId = async (payload: {
    viewId: number | string,
  }): Promise<ICompanyByViewIdResponse | null> => {
    const request = new Request('finsight_web.company.get_company_by_view_id', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  @withServerCache({
    scope: 'Company',
    ttlSeconds: 180,
    maxEntries: 25,
    anonymousOnly: true,
  })
  async getTopCompanies(
    payload: ITopParentsPayload,
  ): Promise<ITopCompanyServerData> {
    let response = await this.rpc.call<RpcSuccess>(new Request(
      'finsight_web.market.company.get_top_companies',
      payload,
    ));
    return response.getResult().payload;
  }

  getCorporateTopCompanies = async (payload: ITopCompaniesPayload): Promise<ITopCompaniesCorporateResponse> => {
    let response = await this.rpc.call<RpcSuccess>(new Request(
      'cfr.company.corp.get_top',
      payload,
    ));
    return response.getResult().payload;
  };

  getTopCompaniesPeriods = async (payload) => {
    let response = await this.rpc.call<RpcSuccess>(new Request(
      'finsight_web.market.company.get_top_companies_ttm_filter',
      payload,
    ));
    return response.getResult().payload;
  };

  getTopCompaniesCorporateTtmFilter = async (
    payload: { filters: ICorporateTopFilters },
  ): Promise<ICorporateTtmFilter[]> => {
    let response = await this.rpc.call<RpcSuccess>(new Request(
      'cfr.company.corp.get_ttm_filter',
      payload,
    ));
    return response.getResult().payload;
  };

  getCompanyById = async (payload: { id?: string } = {}): Promise<ICompany> => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.company.get_company_by_id', payload));

    return response.getResult().payload;
  };

  getCompaniesListAggregate = async (payload = {}): Promise<ICompanyListAggregateResponse> => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.company.get_companies_aggregate', payload));

    return response.getResult().payload;
  };

  getCompaniesList = async (payload: ICompaniesListPayload): Promise<ICompaniesListResponse> => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.company.get_companies', payload));

    return response.getResult().payload;
  };

  getCompanyInformationForMigration = async (payload) => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.company.get_companies_information_for_migration', payload),
    );

    return response.getResult().payload;
  };

  createCompany = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.company.create', payload));

    return response.getResult().payload;
  };

  updateCompany = async (payload = {}) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.company.update', payload));

    return response.getResult().payload;
  };

  removeCompany = async (payload) => {
    const response = await this.rpc.call<RpcSuccess>(new Request('finsight_web.company.delete', payload));

    return response.getResult().payload;
  };
}

export default CompanyRepository;
